/**
 * Holding a world open on this computer.
 *
 * What «Створити» does now. It used to ask our server for a world and then
 * walk into it; the world lives here instead, in this tab, and our server
 * does two things about it: puts a row in the list so people can find it,
 * and introduces them. After that everybody's game goes straight between
 * their machine and this one.
 *
 * **It is not a choice.** There is no "host it yourself" switch, because a
 * switch would mean maintaining both answers and neither of them properly -
 * and because the honest thing to tell somebody is what it costs, which the
 * screen does say: this world uses your computer while you are playing.
 *
 * **It lives as long as the tab does.** Nothing is written to disk: closing
 * the tab is closing the world, and everybody in it is let go. That is worth
 * saying out loud because it is different from how a world on our server
 * behaves, and it is the thing somebody would otherwise find out by losing
 * an evening's building.
 *
 * **The host plays through the same door as everybody else** - a `wirePair`
 * straight into their own world - so there is one code path into a world and
 * it is the one everybody is standing on.
 */
import { Held } from "../core/net/held.js";
import { WarMatch } from "../core/net/match.js";
import { loadMetaFrom, saveMetaTo } from "../core/net/keep.js";
import { browserKeep, type BrowserKeep } from "./browserKeep.js";
import { IndexedDbStore, type SaveStore } from "./saveStore.js";
import { mapById } from "../core/world/maps.js";
import { wirePair } from "../core/net/link.js";
import { SNAPSHOT_HZ } from "../core/net/protocol.js";
import type { GameMap } from "../core/world/maps.js";
import { Signal, Doorbell, knock } from "./peer.js";
import { worldNameFor } from "../core/net/signal.js";
import type { Connection, JoinedWorld } from "./net.js";
import { nameTag } from "./net.js";

/** How often to tell the list how many are in here. Not often: it is a row. */
const COUNT_EVERY_MS = 5000;
/**
 * How often the world is written down.
 *
 * The same half minute our server uses, and only where something changed.
 * Closing the tab writes it too - that is the one that matters, because
 * closing the tab is how a world held here ends.
 */
const KEEP_EVERY_MS = 30_000;

/** Where a world held in this browser is written down. */
export function heldStore(): SaveStore { return new IndexedDbStore(); }

/** How long a finished match stands before it is taken down and deleted. */
const FINISHED_MS = 60_000;

/**
 * What is waiting to be continued under that name, if anything.
 *
 * Read on the home screen so the screen can say "your кремль is here" and
 * when it was last played, rather than making somebody press a button to
 * find out whether anything happens.
 */
export async function heldAlready(
  store: SaveStore, owner: string,
): Promise<{ map: string; savedAt: number } | null> {
  if (owner.length === 0) return null;
  try {
    const keep = await browserKeep(store, worldNameFor(owner));
    const meta = loadMetaFrom(keep);
    if (meta.map === undefined || meta.seed === undefined) return null;
    return { map: meta.map, savedAt: meta.savedAt ?? 0 };
  } catch {
    return null;
  }
}

/**
 * Forget the world kept under that name: every file of it, gone from this
 * browser - "додай можливість почати гру заново, при цьому стару гру
 * видаляй". The next «Почати гру» grows a new one from nothing.
 */
export async function forgetHeld(store: SaveStore, owner: string): Promise<number> {
  if (owner.length === 0) return 0;
  const under = `${worldNameFor(owner)}/`;
  const rows = (await store.list()).filter((row) => row.name.startsWith(under));
  for (const row of rows) await store.remove(row.name);
  return rows.length;
}

export interface Holding {
  /** The world itself, for as long as this tab is open. */
  readonly held: Held;
  /** What our server called it in the list, so it can be handed out. */
  readonly world: string;
  /** Which map it turned out to be - not always the one that was asked for. */
  readonly map: GameMap;
  /** And the word its door asks for, which a continued world brings with it. */
  readonly word: string;
  /** Close it, let everybody go, and take the row off the list. */
  stop(): void;
}

/** Write the world down, and say when. */
async function write(held: Held, keep: BrowserKeep): Promise<void> {
  held.realm.save("holding");
  saveMetaTo(keep, { savedAt: Date.now() });
  await keep.flush();
}

export type HeldResult =
  | { ok: true; holding: Holding; joined: JoinedWorld }
  | { ok: false; why: string };

/**
 * Hold a world of that map, put it on the list, and walk into it.
 *
 * `why` on a refusal is a key the screen translates, never a sentence:
 * "sign-in" where the account did not check out, "not-here" where our server
 * is not introducing anybody, "unreachable" where it did not answer at all.
 */
export async function holdWorld(
  url: string, map: GameMap, word: string, name: string, skinId: string,
  token: string, password: string, net: Connection,
  store: SaveStore = heldStore(),
  match?: { bots: boolean; prep: number; hardcore?: boolean; limit?: number },
): Promise<HeldResult> {
  const signal = new Signal(url);
  if (!await signal.open()) return { ok: false, why: "net-unreachable" };

  /*
   * What is already written down here, before our server is told anything.
   *
   * A world is filed in this browser under the name it will wear in the
   * list, which is the host's own - see `worldNameFor`, and the reason it
   * is derived rather than handed out. That is what makes it readable
   * before the row exists, and the row has to be told what is in it.
   *
   * It went the other way round first, and the live server caught it: the
   * row was put up with the word off the screen, which on a continued
   * world is an empty field nobody typed in, so a world whose door still
   * asked for a password sat in the list with no padlock on it. The map
   * would have gone the same way.
   *
   * The map, the seed and the word all come out of what is written down.
   * Continuing a world means continuing *that* world: a seed chosen afresh
   * would grow a different country under the same name, which `sameWorld`
   * then refuses in terms nobody can act on.
   */
  const keep = await browserKeep(store, worldNameFor(name));
  const before = loadMetaFrom(keep);
  const going = before.map !== undefined && before.seed !== undefined;
  const itsMap = going ? mapById(before.map) : map;
  const itsWord = going ? before.word ?? "" : word;
  /*
   * A seed of its own for a new one, so two worlds of one map are two
   * countries. There is nobody to ask for it: this world has no history.
   */
  const seed = going
    ? (before.seed as number)
    : Math.floor(Math.random() * 2 ** 31);

  /*
   * And now the row, which is what actually names the world.
   *
   * The name is our server's to give out - a page that named its own could
   * call itself `kremlin` and take the traffic meant for a world standing
   * on our machine - and a stranger coming in names the world they picked
   * off the list.
   */
  const doorbell = new Doorbell(signal, url);
  const listed = await doorbell.hold(
    itsMap.id, name, token, itsWord.length > 0, password);
  if (!("world" in listed)) {
    signal.close();
    return { ok: false, why: reason(listed.why) };
  }

  const held = new Held(listed.world, itsMap, seed, keep, itsWord, name);
  // What the maker chose for the match: bots or not, and how long to
  // prepare. The server does the same for a world it makes.
  if (held.realm.war !== null && match !== undefined) {
    held.realm.war = new WarMatch(match.prep, match.bots, match.hardcore === true, match.limit);
  }
  saveMetaTo(keep, {
    map: itsMap.id, seed, word: itsWord, savedAt: Date.now(),
  });

  /* Everybody who is introduced to us arrives here, and nowhere else. */
  doorbell.onWire = (wire): void => {
    held.welcome(wire);
    /*
     * A hello under a name this world keeps for somebody, without that
     * somebody's tag but with a ticket: our server is asked whether the
     * ticket is real, and if it is, the name is theirs again. Everything
     * else goes straight through.
     */
    const route = wire.onMessage;
    wire.onMessage = (message): void => {
      const m = message as { t?: string; name?: unknown; token?: unknown; ticket?: unknown };
      const name = typeof m.name === "string" ? m.name.trim().slice(0, 24) : "";
      if (m.t !== "hello" || typeof m.ticket !== "string" || name.length === 0 ||
        held.realm.nameFits(name, typeof m.token === "string" ? m.token : undefined)) {
        route?.(message);
        return;
      }
      void signal.ask({ t: "vouch", name, ticket: m.ticket }, "vouched").then((said) => {
        route?.(said?.ok === true ? { ...m, token: held.realm.vouchFor(name) } : message);
      });
    };
  };

  /*
   * By the real clock, not by counting beats. A browser that is busy, or a
   * tab in the background, beats less often than fifteen a second, and a
   * world that took each beat as a fifteenth of a second ran slow: on a
   * loaded laptop a twelve-second game was not over after thirty, and a
   * three-hour one would have run on well past three hours. A long gap -
   * a laptop lid - is taken as a quarter of a second, not as a leap.
   */
  let lastBeat = performance.now();
  const beating = setInterval(() => {
    const now = performance.now();
    held.beat(Math.min(0.25, Math.max(0, (now - lastBeat) / 1000)));
    lastBeat = now;
  }, 1000 / SNAPSHOT_HZ);
  const counting = setInterval(() => { doorbell.say(held.players); },
    COUNT_EVERY_MS);
  const keeping = setInterval(() => { void write(held, keep); },
    KEEP_EVERY_MS);
  /*
   * And whenever the tab goes out of sight, which is the one that matters.
   *
   * `pagehide` is where a closing tab would be written down, and a closing
   * tab does not wait: the write is a promise and the browser is gone
   * before it settles. This check was failing exactly there - the world was
   * held, the tab closed, and half the time nothing had reached the disk.
   *
   * `visibilitychange` fires while the page is still alive and running -
   * switching tabs, minimising, locking the phone - and one of those has
   * almost always happened before a tab is closed. Cheap, too: the flush
   * does nothing at all when nothing has changed.
   */
  const whenHidden = (): void => {
    if (document.visibilityState === "hidden") void write(held, keep);
  };
  document.addEventListener("visibilitychange", whenHidden);

  /*
   * A finished match goes, a minute after it ends: long enough to read the
   * table, and then the world is stopped and deleted from this browser -
   * "після закінчення цього часу гра видаляється". A battle that has been
   * won, or whose time ran out, is not somewhere anybody goes back to.
   */
  const ending = setInterval(() => {
    const at = held.realm.finishedAt;
    if (at === null || Date.now() - at < FINISHED_MS) return;
    clearInterval(ending);
    holding.stop();
    // After stop's own last write has landed, so nothing writes it back.
    setTimeout(() => { void forgetHeld(store, name); }, 3000);
  }, 1000);

  const holding: Holding = {
    held, world: listed.world, map: itsMap, word: itsWord,
    stop(): void {
      clearInterval(ending);
      clearInterval(beating);
      clearInterval(counting);
      clearInterval(keeping);
      document.removeEventListener("visibilitychange", whenHidden);
      /*
       * Written before anybody is let go of.
       *
       * `stop` is what a closing tab calls, and the last thing somebody did
       * before closing it is usually the thing they most want back. The
       * flush is a promise and the tab may not wait for it - which is why
       * there is also one every half minute, so the most that can be lost
       * is the last of those.
       */
      write(held, keep).catch(() => { /* the tab is going */ });
      doorbell.stop();
      held.stop();
      signal.close();
    },
  };

  /*
   * And now walk in, down a pair of wires to ourselves.
   *
   * The same hello, the same word, the same everything a stranger sends.
   * A private way in for the host would be a second path into a world, and
   * the person most likely to find a bug in it is the one holding the world.
   */
  /*
   * Written down at once, before anybody has played a minute of it.
   *
   * What this records is which world this is - the map, the seed, the word
   * - and it has to be on the disk from the first second, because the only
   * thing that makes a world continuable is that record. Waiting for the
   * half-minute timer means a tab closed in the first half minute leaves a
   * world that cannot be found again, and the world's first half minute is
   * exactly when somebody is most likely to close the tab.
   */
  await keep.flush();

  const [mine, theirs] = wirePair();
  held.welcome(theirs, "тут");
  const joined = await net.join(
    mine, url, name, skinId, itsWord, held.realm.id, password);
  if (joined === null) {
    holding.stop();
    return { ok: false, why: "net-unreachable" };
  }
  return { ok: true, holding, joined };
}

/** Turn what the switchboard said into something the screen can say. */
export function reason(why: string): string {
  switch (why) {
    case "sign-in": return "net-sign-in";
    case "your-worlds-full": return "net-your-worlds-full";
    case "server-full": return "net-server-full";
    /*
     * The switchboard is not running at all.
     *
     * Which is the honest answer while it is still switched off out there,
     * and stays a possible one afterwards - it is a line in a unit file.
     */
    case "not-here": return "net-no-hosting";
    default: return "net-unreachable";
  }
}

/**
 * Going into a world somebody else is holding.
 *
 * Our server introduces us and then stops being involved: what comes back
 * from `knock` is a channel to their machine, and `join` says the same hello
 * down it that it would say down a socket to us. Everything after that line
 * is identical, which is what all of this was for.
 *
 * Null where we could not get through - their tab closed between the list
 * being drawn and the row being clicked, the introduction timed out, the two
 * machines could not find a way to each other. Being unable to reach
 * somebody is a sentence on the screen; it is never worse than that.
 */
export async function joinHeld(
  url: string, world: string, name: string, skinId: string,
  word: string, password: string, net: Connection,
): Promise<JoinedWorld | null> {
  const signal = new Signal(url);
  if (!await signal.open()) return null;
  /*
   * A ticket first, when signed in: our server's word that this is the
   * account called that, good for this one world. It is what lets a person
   * back in under their name after their browser forgot the world's own tag.
   */
  let ticket = "";
  const signedTag = nameTag(url, "", name);
  if (signedTag.length > 0) {
    const said = await signal.ask({ t: "ticket", name, token: signedTag, world }, "ticket");
    if (typeof said?.ticket === "string") ticket = said.ticket;
  }
  const wire = await knock(signal, url, world);
  /*
   * The signalling socket is done with either way.
   *
   * It introduced us, and an introduction is over once the two people are
   * talking. Holding it open would be a socket on our server for every
   * game being played somewhere else, which is the bill this was meant to
   * stop arriving.
   */
  signal.close();
  if (wire === null) return null;
  return net.join(wire, url, name, skinId, word, world, password, ticket);
}
